import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '@pantry/shared';
import { apiFetch, apiUpload } from '../client';
import { queryKeys } from '../query-keys';
import type { UpdateUserData, AccountDeletionInfo, AvatarUploadResult } from '../types';

/**
 * Fetch the current authenticated user's profile.
 */
async function getCurrentUser(): Promise<User> {
  return apiFetch<User>('/users/me');
}

/**
 * Update the current user's profile.
 */
async function updateCurrentUser(data: UpdateUserData): Promise<User> {
  return apiFetch<User>('/users/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Hook to fetch the current user's profile.
 * Uses a 5 minute stale time since user profile rarely changes.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.users.me(),
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to update the current user's profile.
 * Optimistically updates the cache on success.
 */
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurrentUser,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.users.me(), data);
    },
  });
}

/**
 * Fetch account deletion information.
 * Shows what households would be affected by deleting the account.
 */
async function getAccountDeletionInfo(): Promise<AccountDeletionInfo> {
  return apiFetch<AccountDeletionInfo>('/users/me/deletion-info');
}

/**
 * Delete the current user's account.
 */
async function deleteAccount(): Promise<void> {
  await apiFetch<void>('/users/me', {
    method: 'DELETE',
  });
}

/**
 * Hook to fetch account deletion information.
 * Used to show warnings about what data will be deleted.
 */
export function useAccountDeletionInfo(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.users.deletionInfo(),
    queryFn: getAccountDeletionInfo,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to delete the current user's account.
 * Invalidates all user queries on success.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      // Clear all cached data since user is being deleted
      queryClient.clear();
    },
  });
}

/**
 * Upload an avatar image for the current user.
 */
async function uploadAvatar(file: Blob | File): Promise<AvatarUploadResult> {
  return apiUpload<AvatarUploadResult>('/users/me/avatar', file);
}

/**
 * Delete the current user's avatar.
 */
async function deleteAvatar(): Promise<void> {
  await apiFetch<void>('/users/me/avatar', {
    method: 'DELETE',
  });
}

/**
 * Hook to upload the current user's avatar.
 * Updates the user cache with the new avatar URL on success.
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadAvatar,
    onSuccess: (result) => {
      // Update the cached user with the new avatar URL
      queryClient.setQueryData<User>(queryKeys.users.me(), (oldUser) => {
        if (!oldUser) return oldUser;
        return { ...oldUser, avatarUrl: result.avatarUrl };
      });
    },
  });
}

/**
 * Hook to delete the current user's avatar.
 * Updates the user cache to remove the avatar URL on success.
 */
export function useDeleteAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      // Update the cached user to remove the avatar
      queryClient.setQueryData<User>(queryKeys.users.me(), (oldUser) => {
        if (!oldUser) return oldUser;
        return { ...oldUser, avatarUrl: null };
      });
    },
  });
}
